import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Crop } from '../../types/crops';
import { updateCrop, fetchCrops } from '../../features/crops/cropSlice';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useCropVarieties } from '../../features/cropVarieties/hooks/useCropVarieties';
import Card from '../ui/Card';
import Button from '../ui/Button';

interface Props {
  crop: Crop;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditCropForm: React.FC<Props> = ({ crop, onCancel, onSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { varieties } = useCropVarieties();

  const { values, errors, handleChange, handleSubmit, isValid } = useFormValidation(
    {
      name: crop.name,
      variety_id: crop.variety_id || '',
      planting_date: crop.planting_date || '',
      expected_harvest_date: crop.expected_harvest_date || '',
      status: crop.status,
      notes: crop.notes || '',
    },
    {
      name: (value) => (!value ? t('crops.validation.nameRequired') : ''),
      planting_date: (value) => {
        if (!value) return t('crops.validation.plantingDateRequired');
        if (new Date(value) > new Date()) return t('crops.validation.futureDateError');
        return '';
      },
      expected_harvest_date: (value, allValues) => {
        if (!value) return '';
        if (allValues.planting_date && new Date(value) <= new Date(allValues.planting_date)) {
          return t('crops.validation.harvestDateError');
        }
        return '';
      },
    },
    async (values) => {
      try {
        await dispatch(updateCrop({ id: crop.id, ...values })).unwrap();
        // Fetch crops after updating to refresh the list with complete data
        await dispatch(fetchCrops());
        onSuccess();
      } catch (error) {
        console.error('Error updating crop:', error);
      }
    }
  );

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            {t('crops.cropName')} *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={values.name}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="variety_id" className="block text-sm font-medium text-gray-700">
            {t('crops.variety')}
          </label>
          <select
            id="variety_id"
            name="variety_id"
            value={values.variety_id}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          >
            <option value="">{t('crops.selectVariety')}</option>
            {varieties.map(variety => (
              <option key={variety.id} value={variety.id}>
                {variety.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="planting_date" className="block text-sm font-medium text-gray-700">
            {t('crops.plantingDate')} *
          </label>
          <input
            type="date"
            id="planting_date"
            name="planting_date"
            value={values.planting_date}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.planting_date && (
            <p className="mt-1 text-sm text-red-600">{errors.planting_date}</p>
          )}
        </div>

        <div>
          <label htmlFor="expected_harvest_date" className="block text-sm font-medium text-gray-700">
            {t('crops.harvestDate')}
          </label>
          <input
            type="date"
            id="expected_harvest_date"
            name="expected_harvest_date"
            value={values.expected_harvest_date}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
          {errors.expected_harvest_date && (
            <p className="mt-1 text-sm text-red-600">{errors.expected_harvest_date}</p>
          )}
        </div>

        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            {t('common.status')}
          </label>
          <select
            id="status"
            name="status"
            value={values.status}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          >
            <option value="active">{t('crops.status.active')}</option>
            <option value="harvested">{t('crops.status.harvested')}</option>
            <option value="failed">{t('crops.status.failed')}</option>
            <option value="pending">{t('crops.status.pending')}</option>
          </select>
        </div>

        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            {t('crops.cropNotes')}
          </label>
          <textarea
            id="notes"
            name="notes"
            rows={3}
            value={values.notes}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!isValid}
          >
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default EditCropForm;